import {
  Flex,
  useColorMode,
  useColorStyle,
  Box,
  Button,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Link,
  Menu,
  MenuButton,
  Text,
  MenuList,
  MenuItem,
  Space,
  Icon,
} from '@tonic-one/react';
import React, { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { AccountApi } from '@Apis';
import CSpinner from '@Components/CSpinner';
import { CAS_DOC_LINK, getCasOLHLink } from '@Utils/casDocLink';
import { InfoOIcon } from '@tonic-one/react-icons';

interface InitConfigurationRes {
  dcSiteIds: string[];
  defaultDcSiteId: string;
  isXdrRecommend: boolean;
}
export const DcSite = () => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const borderColor = (
    {
      dark: 'gray:60',
      light: 'gray:30',
    } as any
  )[colorMode];
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [dcSiteIds, setDcSiteIds] = useState<string[]>([]);
  const [defaultDcSiteId, setDefaultDcSiteId] = useState<string>('');
  const [currentDcSiteId, setCurrentDcSiteId] = useState<string>('');
  useEffect(() => {
    setLoading(true);
    AccountApi.getInitConfiguration()
      .then((res: { data: InitConfigurationRes }) => {
        console.log(res.data);
        setDcSiteIds(res.data.dcSiteIds);
        setDefaultDcSiteId(res.data.defaultDcSiteId);
        setCurrentDcSiteId(res.data.defaultDcSiteId);
      })
      .catch((error) => {
        console.error(error);
        window.showNotification({
          titleMsg: t('login.initConfiguration.loadError'),
          severity: 'error',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [t]);

  const save = useCallback(async () => {
    try {
      const res = await AccountApi.updateInitConfiguration(currentDcSiteId);
      if (res.data.loginUrl) {
        window.location.href = res.data.loginUrl;
      } else {
        window.showNotification({
          titleMsg: t('login.initConfiguration.internalError'),
          severity: 'error',
        });
      }
    } catch (error) {
      window.showNotification({
        titleMsg: t('login.initConfiguration.internalError'),
        severity: 'error',
      });
    }
  }, [currentDcSiteId, t]);

  const siteNameMapping = useCallback(
    (siteId: string | undefined) => {
      if (siteId) {
        let siteName = '';
        const siteIdSplit = siteId.split('_');

        if (siteIdSplit[0] === 'prod') {
          const siteKey = `common.dataCenter.${siteIdSplit[1].toLowerCase()}`;
          siteName = t(siteKey);
        } else if (siteIdSplit[1] === 'wu') {
          switch (siteIdSplit[0]) {
            case 'dev':
              siteName = 'DEV';
              break;
            case 'stg':
              siteName = 'STG';
              break;
            case 'sit':
              siteName = 'SIT';
              break;
            case 'beta':
              siteName = 'BETA';
              break;
            default:
              break;
          }
        }
        return siteName;
      } else {
        return '';
      }
    },
    [t]
  );
  const Block = (props: any) => {
    const [colorMode] = useColorMode();
    const [colorStyle] = useColorStyle({ colorMode });
    return (
      <Flex
        px="4x"
        py="3x"
        alignItems="flex-start"
        justifyContent="center"
        flexDirection="column"
        backgroundColor={colorStyle.background.secondary}
        {...props}
      />
    );
  };
  return (
    <>
      {loading && <CSpinner />}
      <Box>
        <Flex
          justifyContent="center"
          alignItems="center"
          bg={colorStyle.background.primary}
          height="100vh"
        >
          <Flex width="40%">
            <Box flex="auto">
              <Flex flexDirection="column">
                <ModalContent overflow="visible">
                  <ModalHeader>{t('login.text.initConfiguration')}</ModalHeader>
                  <ModalBody>
                    <Box>
                      <Block
                        height="40px"
                        border={1}
                        borderColor={borderColor}
                        bg={colorStyle.background.tertiary}
                      >
                        Serving Site
                      </Block>
                      <Block border={1} borderColor={borderColor} p="18px 18px">
                        <Box>
                          <Trans i18nKey="login.text.initConfiguration.desc">
                            placeholder
                            <Link
                              href={getCasOLHLink(
                                CAS_DOC_LINK.init_config,
                                i18next.language
                              )}
                              target="_blank"
                              display="inline"
                            >
                              placeholder
                            </Link>
                            placeholder
                          </Trans>
                        </Box>

                        <Flex alignItems="center" m="8px 0px 12px 0px">
                          <Text fontWeight={600}>{t('login.text.site')}</Text>
                          <Space width="12px" />
                          <Menu>
                            <MenuButton variant="secondary">
                              <Text>{siteNameMapping(currentDcSiteId)}</Text>
                            </MenuButton>
                            <MenuList width="max-content">
                              {dcSiteIds.map((dcSiteId) => (
                                <MenuItem
                                  value={dcSiteId}
                                  key={dcSiteId}
                                  onClick={(e: any) => {
                                    setCurrentDcSiteId(
                                      e.currentTarget.getAttribute('value')
                                    );
                                    console.log(
                                      e.currentTarget.getAttribute('value')
                                    );
                                  }}
                                >
                                  <Text>
                                    {dcSiteId === defaultDcSiteId
                                      ? `${siteNameMapping(dcSiteId)} (${t(
                                          'common.dataCenter.recommend'
                                        )})`
                                      : siteNameMapping(dcSiteId)}{' '}
                                  </Text>
                                </MenuItem>
                              ))}
                            </MenuList>
                          </Menu>
                        </Flex>
                        <Flex alignItems="center">
                          <Icon as={InfoOIcon} color={colorStyle.color.info} />
                          <Space width="4px" />
                          <Space>{t('globalSetting.note.title')}</Space>
                        </Flex>
                        <Text>
                          <Space color={colorStyle.color.secondary}>
                            {t('login.initConfiguration.note.desc')}
                          </Space>
                        </Text>
                      </Block>
                    </Box>
                  </ModalBody>
                  <ModalFooter>
                    <Button variant="primary" minWidth="20x" onClick={save}>
                      {t('common.button.save')}
                    </Button>
                  </ModalFooter>
                </ModalContent>
              </Flex>
            </Box>
          </Flex>
        </Flex>
      </Box>
    </>
  );
};
